// dependencies
import "ag-grid-enterprise";
import React, { useRef, useEffect } from "react";
import { Link } from "yarrr";
import { AgGridReact } from "ag-grid-react"; // React Grid Logic
import { useWindowSize } from "@uidotdev/usehooks";

// components
import Spacer from "../../../Components/Spacer";
import { useCompanyMatrix, useFeatureFlags } from "../../../Api/hooks";
import { useEnvironment } from "../../../Hooks";

// css
import css from "./CompanyMatrix.styl";
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme

const CompanyLogoRenderer = ({ data }) => {
	const environment = useEnvironment();
	const domain = environment === "production" ? "https://media.performyard.com" : "https://dev-media.performyard.com";

	return (
		<span style={{ display: "flex", height: "100%", width: "100%", alignItems: "center" }}>
			{data && (
				<img
					alt={`${data.name} Logo`}
					src={`${domain}/company-logo/${data._id}/`}
					style={{
						display: "block",
						width: "25px",
						height: "auto",
						maxHeight: "50%",
						marginRight: "12px",
						filter: "brightness(1.1)",
					}}
				/>
			)}
			<p style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
				<Link className={css.companyLink} href={`/companies/${data._id}/feature-flags`}>
					{data.name}
				</Link>
			</p>
		</span>
	);
};

const DEFAULT_COLUMN_DEF = {
	sortable: true,
	filter: true,
	floatingFilter: true,
	// filter: "agTextColumnFilter",
};

const COLUMNS = [
	{
		field: "name",
		headerName: "Company",
		pinned: "left",
		sort: "asc",
		cellRenderer: CompanyLogoRenderer,
		cellStyle: (params) => {
			return { color: params.data.branding.iconColor, backgroundColor: params.data.branding.navColor };
		},
	},
	{
		field: "maxUsers",
		headerName: "Max Users",
	},
	{
		field: "activeUsers",
		headerName: "Active Users",
	},
	{
		field: "billingAdmins",
		headerName: "Billing Admins",
	},
	{
		field: "admins",
		headerName: "Admins",
	},
	{
		field: "feedbackHashtags",
		headerName: "Feedback Hashtags",
	},
	{
		field: "timezone",
		headerName: "Timezone",
	},
	{
		field: "session_timeout",
		headerName: "Session Timeout",
	},
	{
		field: "featureFlagsv1.hr-integration",
		headerName: "HRIS Integration",
	},
	{
		field: "featureFlagsv1.engagement",
		headerName: "Engagement",
	},
	{
		field: "featureFlagsv1.api-key",
		headerName: "API",
	},
	{
		field: "featureFlagsv1.scim_api",
		headerName: "SCIM",
	},
	{
		field: "featureFlagsv1.integration-sftp",
		headerName: "SFTP",
	},
	{
		field: "featureFlagsv1.slack-notifications",
		headerName: "Slack",
	},
	{
		field: "featureFlagsv1.teams-integration",
		headerName: "Microsoft Teams",
	},
	{
		field: "featureFlagsv1.org-chart",
		headerName: "Org Chart",
	},
	{
		field: "featureFlagsv1.status-reports",
		headerName: "Status Reports",
	},
	{
		field: "featureFlagsv1.saml",
		headerName: "SAML",
	},
	{
		field: "featureFlagsv1.groups",
		headerName: "Groups",
	},
	{
		field: "featureFlagsv1.goals",
		headerName: "Goals",
	},
	{
		field: "featureFlagsv1.enforce-login-methods",
		headerName: "Enforce Login Methods",
	},
	{
		headerName: "Email and Password Login",
		cellRenderer: ({ data }) => {
			return data.authenticationSettings?.authentication_methods?.email_and_password?.state === "enabled"
				? "Enabled"
				: "Disabled";
		},
	},
	{
		headerName: "Google Login",
		cellRenderer: ({ data }) => {
			return data.authenticationSettings?.authentication_methods?.google?.state === "enabled"
				? "Enabled"
				: "Disabled";
		},
	},
	{
		headerName: "Office365 Login",
		cellRenderer: ({ data }) => {
			return data.authenticationSettings?.authentication_methods?.office365?.state === "enabled"
				? "Enabled"
				: "Disabled";
		},
	},
	{
		field: "taxAddress.company_code",
		headerName: "Company Code",
	},
	{
		field: "taxAddress.address_line_1",
		headerName: "Address Line 1",
	},
	{
		field: "taxAddress.address_line_2",
		headerName: "Address Line 2",
	},
	{
		field: "taxAddress.zip",
		headerName: "Zip Code",
	},
	{
		field: "taxAddress.has_tax_exclusion",
		headerName: "Sales Tax Exclusion",
	},
];

const statusBar = {
	statusPanels: [
		{ statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
		{ statusPanel: "agTotalRowCountComponent", align: "center" },
		{ statusPanel: "agFilteredRowCountComponent" },
		{ statusPanel: "agSelectedRowCountComponent" },
		{ statusPanel: "agAggregationComponent" },
	],
};

const CompanyFeatureFlagMatrix = () => {
	const gridRef = useRef(null);
	const size = useWindowSize();
	const environment = useEnvironment();

	const featureFlagsQuery = useFeatureFlags(environment);
	const companyMatrixQuery = useCompanyMatrix(environment);

	useEffect(() => {
		if (!gridRef.current) return;
		if (!gridRef.current.api) return;

		if (companyMatrixQuery.isLoading || featureFlagsQuery.isLoading) {
			gridRef.current.api.showLoadingOverlay();
			return;
		}

		gridRef.current.api.hideOverlay();
	}, [companyMatrixQuery.data, featureFlagsQuery.data]);

	const columns = [...COLUMNS];

	return (
		<div className={css.content}>
			<div className={css.sectionInformation}>
				<div className={css.title}>Company Matrix</div>

				<Spacer size={3} />

				<div className={css.gridContainer}>
					<div className="ag-theme-quartz" style={{ height: size.height - 180 }}>
						<AgGridReact
							ref={gridRef}
							animateRows={false}
							defaultColDef={DEFAULT_COLUMN_DEF}
							rowData={companyMatrixQuery?.data?.companies ?? []}
							columnDefs={columns}
							isRowSelectable={() => false}
							statusBar={statusBar}
							enableRangeSelection={true}
							rowSelection="multiple"
							overlayLoadingTemplate={
								'<div aria-live="polite" aria-atomic="true" style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CompanyFeatureFlagMatrix;
